// "Edit Request" button — opens Prestige's own Google Form with the question
// already identified, so a tutor who spots a bad item does not have to describe
// which item it was.
//
// Prestige owns the form, the responses sheet and all triage. Nothing is stored
// on our side: this module is only the link builder plus the org gate.

// Long-form id of https://forms.gle/CPRTgcfCs324HLZz8 (prefill only works on the
// /forms/d/e/<id>/viewform URL, not on the forms.gle short link).
const FORM_ID = '1FAIpQLSdql2wRVW5bmElT62eQSC9ZBKrKqgLt0syCLQOCSwIaiD5oDQ';

// Google's per-field ids, read off the live form. They are stable for the life
// of the form, but a field DELETED and re-added gets a new one -- if responses
// start arriving with an empty Question ID column, re-read them (see
// docs/feature/20260921-prestige-edit-request-form-button-plan.md for how).
//
// The form's two other fields (Issue Type, Describe the Issue) are deliberately
// left blank for the tutor, and its Screenshot field is a file upload, which
// Google does not allow prefilling at all.
const FIELD_QUESTION_ID = 'entry.819358455';
const FIELD_NAME = 'entry.1401992858';

// Which organizations see the button: the orgs that Prestige tests are actually
// provisioned to, so this list mirrors `orgs` in scripts/addOrganizationTest.py
// and scripts/addOrganizationACT.py (identical to each other), NOT
// PRESTIGE_ORG_ALIASES in tests/constants.py. That constant is the *billing*
// list -- it carries 'prestigeonline', which gets no Prestige tests, and omits
// 'prestigehq', which does. Reporting a bad question follows who can see the
// question, not who is invoiced for it.
//
// Matched case-insensitively on purpose: the real aliases include BOTH
// 'prestigeon' and 'prestigeON', and near-misses like 'prestigeonl' vs
// 'prestigeonline' are separate organizations, so a bare === against one
// spelling silently hides the button from a branch that should have it.
// Compared on `alias` rather than `urlSlug` because urlSlug is nullable.
const ORG_ALIASES = [
    'prestige',          // "Prestige Institute" -- the dev seed DB's alias; no prod row uses it
    'prestigehq',
    'prestigesuwanee',
    'prestigealpharetta',
    'prestigebayside',
    'prestigepp',
    'prestigecloster',
    'prestigehartsdale',
    'prestigeon',
    'prestigeON',        // a real second row, not a typo -- spelled out like the server lists do
    'prestigeconsulting',
    // 'prestigeonl' is "Prestige online" (Organizations id=503). Deliberately
    // NOT 'prestigeonline' (id=2, "Prestige Online"), a different organization
    // that scripts/addOrganizationTest.py also leaves out.
    'prestigeonl'
].map(alias => alias.toLowerCase());

type EditRequestUser = {
    firstName?: string;
    lastName?: string;
    organizations?: { alias?: string }[];
};

export const canRequestEdits = (user: EditRequestUser | null | undefined): boolean => (
    (user?.organizations || []).some(organization => (
        !!organization?.alias && ORG_ALIASES.includes(organization.alias.toLowerCase())
    ))
);

export const buildEditRequestUrl = (
    questionId: string,
    user?: EditRequestUser | null
): string => {
    const params = new URLSearchParams({ usp: 'pp_url' });

    params.set(FIELD_QUESTION_ID, questionId);

    const name = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim();
    if (name) params.set(FIELD_NAME, name);

    return `https://docs.google.com/forms/d/e/${FORM_ID}/viewform?${params.toString()}`;
}
